import {useCallback} from 'react';
import {useDefaultOnError} from '../useDefaultOnError';
import {State} from '@synako/halitrek';
import {ArticlePanierAjouteEventDesc} from 'api-types/ffconnect-hal';
import {Trans, useTranslation} from 'react-i18next';
import {useCommand} from '@synako/haligator';
import {PanierLink} from '@/domains/panier/controllers/PanierLink';
import {useNavigate} from 'react-router-dom';
import NiceModal from '@ebay/nice-modal-react';
import {ModaleErreurDisponibilite} from '@/domains/cadencier/components/ModaleErreurDisponibilite.tsx';
import {useLivraisonsPossiblesContext} from 'ff-common';
import {useClient} from '@/haligator-factory';
import {AjouterAuPanierCommand} from 'api-types/ffconnect';
import {TextToastCommand} from 'ff-common/src/types/TextToastCommand';

export type AjouterAuPanier = ReturnType<typeof useAjouterAuPanier>;

export const useAjouterAuPanier = (
  livraison_id: string | undefined,
  textToast: TextToastCommand,
  onProductClick?: (code_article: string) => void, // Fonction pour ouvrir la fiche details d'un produit dans la prise de commande
  customOnSuccess?: (
    event: State<ArticlePanierAjouteEventDesc>,
  ) => void | Promise<void>, // Callback de succès personnalisé pour le backoffice
) => {
  const {t} = useTranslation();
  const navigate = useNavigate();
  const onError = useDefaultOnError(textToast);
  const {setSelectedMarketplaceId} = useLivraisonsPossiblesContext();
  // Exposé pour `submitAsync` (qui ne passe pas par `onSuccess`).
  const notifierAjoutEtRupture = useCallback(
    async (event: State<ArticlePanierAjouteEventDesc>) => {
      setSelectedMarketplaceId(null);
      const {date_de_livraison} = event.data.data.livraison.tolede;
      textToast(
        [
          <Trans
            t={t}
            key='ajoute-panier-toast'
          >
            {t('toast.ajoute_panier_date', {date: new Date(date_de_livraison)})}
          </Trans>,
          <PanierLink
            key='voir-panier-cta'
            t={t}
            date_de_livraison={date_de_livraison}
            navigate={navigate}
          />,
        ],
        {id: 'ajoute-panier-toast'},
      );

      try {
        const dispo = await event.follow('disponibilite_article').get();
        const ligneDePanier = event.follow('ligne_de_panier');
        const tolede_disponibilite = dispo.data.tolede.disponibilite;
        if (
          tolede_disponibilite === 'rupture' ||
          tolede_disponibilite === 'delai_de_prepa_long' ||
          dispo.data.quantite_manquante
        ) {
          NiceModal.show(ModaleErreurDisponibilite, {
            dispo,
            ligneDePanier,
            date_de_livraison,
            onProductClick,
          });
        }
      } catch (_error) {
        textToast(<Trans t={t}>{t('error.controle_dispo')}</Trans>, {
          icon: 'alert',
          id: 'controle-dispo-error-toast',
        });
      }
    },
    [textToast, t, navigate, onProductClick, setSelectedMarketplaceId],
  );

  const onSuccess = useCallback(
    async (event: State<ArticlePanierAjouteEventDesc>) => {
      // Si un callback personnalisé est fourni (backoffice), l'utiliser au lieu du comportement par défaut
      if (customOnSuccess) {
        await customOnSuccess(event);
        return;
      }

      await notifierAjoutEtRupture(event);
    },
    [customOnSuccess, notifierAjoutEtRupture],
  );

  const client = useClient();
  const {submit, submitAsync, ...rest} = useCommand(
    client,
    'c:ajouter-au-panier',
    {
      onError,
      onSuccess,
    },
  );
  return {
    ...rest,
    submit: (payload: AjouterAuPanierCommand) => {
      submit(payload, {livraison_id});
    },
    submitAsync: (payload: AjouterAuPanierCommand) => {
      return submitAsync(payload, {livraison_id});
    },
    notifierAjoutEtRupture,
  };
};
